import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import { Edit20Px, useToast } from "@qubit/autoparts";
import { useCallback, useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import type { WorkstationSettingsFormValues } from "~/features/preview/workstation/hooks/useWorkstationConfigForm";
import { sortWorkstationsByGrid } from "~/features/preview/workstation/settingsWorkstation.logic";
import {
  settingsInputMinHeightPx,
  settingsOverviewHelperSx,
  settingsSelectSx,
  settingsSiteSelectMaxWidthPx
} from "~/features/settings/settings.styles";
import {
  selectSelectedGridId,
  setDropdownWorkstationId
} from "~/features/workstationConfig/workstationConfig.slice";
import { useSetThisWorkstation } from "~/hooks/useSetThisWorkstation";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { selectDropdownWorkstation } from "~/redux/selectors/workstationConfigSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import { EditWorkstationName } from "./EditWorkstationName";

type Props = {
  isEditingName: boolean;
  setIsEditingName: (isEditingName: boolean) => void;
};

export function SiteWorkstationAssignment({
  isEditingName,
  setIsEditingName
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { successToast, errorToast } = useToast();
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const userFc = useAppSelector(selectUsersFulfillmentCenter);
  const selectedGridId = useAppSelector(selectSelectedGridId);
  const { setThisWorkstation, isLoading: isSettingWorkstation } =
    useSetThisWorkstation();
  const dropdownWorkstation = useAppSelector(selectDropdownWorkstation);
  // Prefer form deviceId so Select shows the saved name before list cache refetches
  const [formWorkstationId, formDeviceId] = useWatch<
    WorkstationSettingsFormValues,
    ["workstationId", "deviceId"]
  >({
    name: ["workstationId", "deviceId"]
  });

  useEffect(() => {
    setIsEditingName(false);
  }, [dropdownWorkstation?.id, setIsEditingName]);

  const { data: workstations, isLoading } = useGetWorkstationsQuery(undefined, {
    skip: !userFc
  });
  const sortedWorkstations = useMemo(
    () =>
      workstations === undefined
        ? undefined
        : sortWorkstationsByGrid(workstations, selectedGridId),
    [workstations, selectedGridId]
  );

  const handleWorkstationChanged = useCallback(
    (e: SelectChangeEvent) => {
      dispatch(setDropdownWorkstationId(e.target.value));
    },
    [dispatch]
  );

  const isWorkstationDirty = dropdownWorkstation?.id !== siteWorkstation?.id;
  const isCurrentWsAssigned =
    siteWorkstation != null && dropdownWorkstation?.id === siteWorkstation.id;

  const handleSetThisWorkstationClicked = useCallback(async () => {
    if (isCurrentWsAssigned) {
      try {
        await setThisWorkstation(null, true);
        successToast(t("workstation unset"), {
          description: t("workstation unset description")
        });
      } catch (error) {
        errorToast(getMessageFromRtkError(error));
      }
      return;
    }

    if (!isWorkstationDirty) {
      return;
    }
    try {
      await setThisWorkstation(dropdownWorkstation ?? null, true);
      if (dropdownWorkstation) {
        successToast(t("workstation set"), {
          description: t("workstation set description", {
            workstation: dropdownWorkstation.deviceId
          })
        });
      } else {
        successToast(t("workstation unset"), {
          description: t("workstation unset description")
        });
      }
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  }, [
    isCurrentWsAssigned,
    isWorkstationDirty,
    setThisWorkstation,
    dropdownWorkstation,
    successToast,
    errorToast,
    t
  ]);

  const assignDisabled =
    isSettingWorkstation || (!isWorkstationDirty && !isCurrentWsAssigned);
  const buttonLabel = isCurrentWsAssigned
    ? t("unassign workstation")
    : t("assign workstation");

  const helperText = t("select workstation id matches port helper");

  if (!sortedWorkstations && isLoading) {
    return (
      <Stack id="ws-site-assignment-loading" spacing={2}>
        <Typography variant="body2">{t("workstation")}</Typography>
      </Stack>
    );
  }

  if (!sortedWorkstations?.length && !isLoading) {
    return (
      <Stack id="ws-site-assignment-empty" spacing={2}>
        <Typography variant="body2">{t("workstation")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("no workstations found")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack id="ws-site-assignment-root" spacing={1} sx={{ width: "100%" }}>
      <Box
        id="ws-site-assignment-assign-row"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flexWrap: { md: "nowrap" },
          alignItems: { md: "center" },
          width: "100%",
          minWidth: 0,
          columnGap: { xs: 0, md: 2 },
          rowGap: { xs: 1.5, md: 0 }
        }}
      >
        <Box
          id="ws-site-assignment-control-cluster"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { md: "center" },
            flex: { md: "1 1 auto" },
            minWidth: 0,
            width: "100%",
            gap: 1
          }}
        >
          {isAdmin && isEditingName && dropdownWorkstation ? (
            <EditWorkstationName />
          ) : (
            <>
              <Box
                id="ws-site-assignment-select-wrap"
                sx={{
                  width: "100%",
                  maxWidth: {
                    xs: "100%",
                    md: settingsSiteSelectMaxWidthPx
                  },
                  minWidth: 0,
                  flex: { md: "0 1 auto" }
                }}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ "& > .MuiOutlinedInput-root": { marginTop: 0 } }}
                >
                  <Select
                    id="assign-workstation-select-preview"
                    fullWidth
                    variant="outlined"
                    value={dropdownWorkstation?.id ?? ""}
                    onChange={handleWorkstationChanged}
                    displayEmpty
                    inputProps={{
                      "aria-label": String(t("workstation")),
                      "aria-describedby": "ws-site-assignment-helper"
                    }}
                    sx={settingsSelectSx}
                    renderValue={(id) => {
                      if (id === "") {
                        return (
                          <Typography
                            variant="body2"
                            component="span"
                            color="text.secondary"
                          >
                            <em>{t("no workstation selected")}</em>
                          </Typography>
                        );
                      }
                      const ws = sortedWorkstations?.find((w) => w.id === id);
                      if (!ws) {
                        return "";
                      }
                      const displayName =
                        id === formWorkstationId && formDeviceId
                          ? formDeviceId
                          : ws.deviceId;
                      return (
                        <Typography variant="body2" component="span" noWrap>
                          {displayName}
                        </Typography>
                      );
                    }}
                  >
                    {sortedWorkstations?.map((workstation) => (
                      <MenuItem key={workstation.id} value={workstation.id}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          width="100%"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ minWidth: 0 }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ minWidth: 0, flex: "1 1 auto" }}
                          >
                            <Typography variant="body2" noWrap>
                              {workstation.deviceId}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {workstation.autostoreGridName}
                            </Typography>
                          </Stack>
                          <Chip
                            size="small"
                            label={
                              workstation.status === "Active"
                                ? t("workstation status chip in use")
                                : workstation.status
                            }
                            color={
                              workstation.status === "Active"
                                ? "warning"
                                : undefined
                            }
                          />
                        </Stack>
                      </MenuItem>
                    ))}
                    <MenuItem value="">
                      <em>{t("no workstation selected")}</em>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {isAdmin && (
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!dropdownWorkstation}
                  onClick={() => setIsEditingName(true)}
                  aria-label={t("edit workstation name")}
                  sx={{
                    flexShrink: 0,
                    minHeight: settingsInputMinHeightPx,
                    minWidth: settingsInputMinHeightPx,
                    boxSizing: "border-box",
                    px: 1,
                    ...(!dropdownWorkstation && {
                      bgcolor: "transparent",
                      color: "secondary.main",
                      border: "1px solid",
                      borderColor: "secondary.main",
                      boxShadow: "none"
                    })
                  }}
                >
                  <Edit20Px
                    style={{ fill: "currentColor" }}
                    width={20}
                    height={20}
                  />
                </Button>
              )}

              <Box
                id="ws-site-assignment-button-wrap"
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-end", md: "flex-start" },
                  alignItems: "center",
                  flexShrink: 0,
                  minWidth: 0
                }}
              >
                <Button
                  variant="contained"
                  color={!isCurrentWsAssigned ? "primary" : "secondary"}
                  loading={isSettingWorkstation}
                  disabled={assignDisabled}
                  onClick={() => void handleSetThisWorkstationClicked()}
                  aria-label={buttonLabel}
                  sx={{
                    flexShrink: 0,
                    minHeight: settingsInputMinHeightPx,
                    boxSizing: "border-box",
                    maxWidth: "none",
                    width: "auto"
                  }}
                >
                  {buttonLabel}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {!(isAdmin && isEditingName) && (
        <Typography
          id="ws-site-assignment-helper"
          variant="body2"
          sx={settingsOverviewHelperSx}
        >
          {helperText}
        </Typography>
      )}
    </Stack>
  );
}
